import { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { motion, AnimatePresence } from 'framer-motion'
import { Upload, X, Image, Video, Link as LinkIcon, Loader2, CheckCircle, AlertCircle } from 'lucide-react'
import { uploadFile } from '../../services/firebase'

const MediaUploader = ({ onUpload, existingMedia = [] }) => {
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({})
  const [videoUrl, setVideoUrl] = useState('')
  const [videoCaption, setVideoCaption] = useState('')
  const [showVideoForm, setShowVideoForm] = useState(false)
  const [error, setError] = useState(null)

  const onDrop = useCallback(async (acceptedFiles) => {
    setUploading(true)
    setError(null)

    const newMedia = []

    for (let i = 0; i < acceptedFiles.length; i++) {
      const file = acceptedFiles[i]
      const fileId = `${Date.now()}_${i}`

      setUploadProgress(prev => ({ ...prev, [fileId]: { name: file.name, progress: 0 } }))

      try {
        const path = `gallery/${Date.now()}_${file.name}`
        const url = await uploadFile(file, path)

        const isVideo = file.type.startsWith('video/')

        newMedia.push({
          type: isVideo ? 'video' : 'image',
          url,
          thumbnail: isVideo ? null : url,
          caption: '',
          fileName: file.name,
        })

        setUploadProgress(prev => ({ ...prev, [fileId]: { ...prev[fileId], progress: 100, done: true } }))
      } catch (err) {
        console.error('Upload error:', err)
        setUploadProgress(prev => ({ ...prev, [fileId]: { ...prev[fileId], error: true } }))
        setError(`Failed to upload ${file.name}`)
      }
    }

    if (newMedia.length > 0) {
      onUpload([...existingMedia, ...newMedia])
    }

    setTimeout(() => {
      setUploadProgress({})
      setUploading(false)
    }, 1500)
  }, [existingMedia, onUpload])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
      'video/*': ['.mp4', '.webm', '.mov'],
    },
    maxSize: 100 * 1024 * 1024, // 100MB
  })

  const handleAddVideo = () => {
    if (!videoUrl.trim()) return

    const newMedia = {
      type: 'video',
      url: videoUrl.trim(),
      caption: videoCaption.trim(),
      isExternal: true,
    }

    onUpload([...existingMedia, newMedia])
    setVideoUrl('')
    setVideoCaption('')
    setShowVideoForm(false)
  }

  const handleRemoveMedia = (index) => {
    const updated = existingMedia.filter((_, i) => i !== index)
    onUpload(updated)
  }

  const handleUpdateCaption = (index, caption) => {
    const updated = existingMedia.map((item, i) =>
      i === index ? { ...item, caption } : item
    )
    onUpload(updated)
  }

  return (
    <div className="space-y-6">
      {/* Upload Zone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-colors ${
          isDragActive
            ? 'border-primary-500 bg-primary-50'
            : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
        }`}
      >
        <input {...getInputProps()} />
        <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragActive ? 'text-primary-500' : 'text-gray-400'}`} />
        <p className="text-lg font-medium text-dark-100 mb-1">
          {isDragActive ? 'Drop files here...' : 'Drag & drop photos or videos'}
        </p>
        <p className="text-sm text-gray-500 mb-4">or click to browse</p>
        <p className="text-xs text-gray-400">Supports: JPG, PNG, GIF, MP4, WebM (Max 100MB)</p>
      </div>

      {/* Upload Progress */}
      <AnimatePresence>
        {Object.entries(uploadProgress).length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-2"
          >
            {Object.entries(uploadProgress).map(([id, file]) => (
              <div key={id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                {file.done ? (
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                ) : file.error ? (
                  <AlertCircle className="w-5 h-5 text-red-500" />
                ) : (
                  <Loader2 className="w-5 h-5 text-primary-500 animate-spin" />
                )}
                <span className="flex-1 text-sm truncate">{file.name}</span>
                <span className="text-xs text-gray-500">
                  {file.done ? 'Done' : file.error ? 'Failed' : 'Uploading...'}
                </span>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Video URL */}
      <div className="border border-gray-200 rounded-xl p-4">
        <button
          type="button"
          onClick={() => setShowVideoForm(!showVideoForm)}
          className="flex items-center gap-2 text-primary-600 font-medium hover:text-primary-700"
        >
          <LinkIcon className="w-5 h-5" />
          Add YouTube/Vimeo Video
        </button>

        <AnimatePresence>
          {showVideoForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 space-y-3"
            >
              <input
                type="url"
                placeholder="YouTube or Vimeo URL"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <input
                type="text"
                placeholder="Caption (optional)"
                value={videoCaption}
                onChange={(e) => setVideoCaption(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={handleAddVideo}
                disabled={!videoUrl.trim()}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Video
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      {/* Media Preview Grid */}
      {existingMedia.length > 0 && (
        <div>
          <h4 className="font-medium text-dark-100 mb-3">
            Gallery Media ({existingMedia.length})
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {existingMedia.map((item, index) => (
              <div key={index} className="relative group">
                <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                  {item.type === 'video' ? (
                    <div className="w-full h-full bg-dark-200 flex items-center justify-center">
                      <Video className="w-8 h-8 text-white" />
                    </div>
                  ) : (
                    <img
                      src={item.thumbnail || item.url}
                      alt={item.caption || ''}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>

                {/* Remove Button */}
                <button
                  type="button"
                  onClick={() => handleRemoveMedia(index)}
                  className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                >
                  <X className="w-4 h-4" />
                </button>

                {/* Caption Input */}
                <input
                  type="text"
                  placeholder="Caption..."
                  value={item.caption || ''}
                  onChange={(e) => handleUpdateCaption(index, e.target.value)}
                  className="mt-2 w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-primary-500"
                />

                {/* Type Badge */}
                <span className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                  item.type === 'video'
                    ? 'bg-secondary-500 text-white'
                    : 'bg-primary-500 text-white'
                }`}>
                  {item.type === 'video' ? 'Video' : 'Photo'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default MediaUploader
